import { useState, useRef, forwardRef, useCallback, Fragment } from 'react';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    useListNavigation,
    FloatingFocusManager,
    FloatingPortal,
    offset,
    flip,
    size,
    autoUpdate,
    useMergeRefs,
} from '@floating-ui/react';
import { cn } from 'Utils/cn';
import PropTypes from 'prop-types';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import { AnimatePresence, motion } from 'framer-motion';
import selectVariants from './SelectVariants';
import ButtonClear from 'Components/Atoms/ButtonClear/ButtonClear';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import SelectFilter from './SelectFilter';
import { hasMatch } from 'Utils/strings';
import { useTranslation } from 'react-i18next';
import FloatLabel from '../FloatLabel/FloatLabel';

const Select = forwardRef(function Select(props, ref) {
    const {
        options,
        value,
        onChange,
        placeholder = '',
        size: selectSize,
        className,
        label,
        icon,
        enableClear = false,
        onClear,
        error,
        enableFilter = false,
        disabled,
        highlight,
        actions = [],
        dropdownClassName,
        dropdownWidth = 'trigger',
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);
    const [query, setQuery] = useState('');
    const { t } = useTranslation('common');
    const [isHighlighted, setIsHighlighted] = useState(highlight || false);
    const [activeIndex, setActiveIndex] = useState(null);
    const listRef = useRef([]);

    const handleFilter = (ev) => {
        setQuery(ev.target.value);
        setActiveIndex(null);
    };

    const isGrouped = options.length > 0 && Array.isArray(options[0]?.options);
    const flatOptions = isGrouped ? options.flatMap((g) => g.options) : options;

    const selected = flatOptions.find((opt) => opt.value === value);

    const dropdownWidthKey = dropdownWidth === 'trigger' ? 'width' : 'minWidth';

    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open: isOpen,
        strategy: 'fixed',
        placement: 'bottom-start',
        onOpenChange: (isOpen) => {
            setIsHighlighted(false);
            setIsOpen(isOpen);
            if (!isOpen) {
                setActiveIndex(null);
                setQuery('');
            }
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(4),
            flip(),
            size({
                apply({ rects, elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        [dropdownWidthKey]: `${rects.reference.width}px`,
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    const listNav = useListNavigation(context, {
        listRef,
        activeIndex,
        onNavigate: setActiveIndex,
        virtual: enableFilter,
        loop: true,
        focusItemOnOpen: true,
    });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
        listNav,
    ]);

    const mergedRef = useMergeRefs([ref, refs.setReference, buttonRef]);
    const hasError = !!error;

    const optionsFiltered = query.trim()
        ? isGrouped
            ? options
                  .map((group) => ({ ...group, options: group.options.filter((opt) => hasMatch(opt.label, query)) }))
                  .filter((group) => group.options.length > 0)
            : options.filter((option) => hasMatch(option.label, query))
        : options;
    const filteredFlatOptions = isGrouped
        ? optionsFiltered.flatMap((g) => g.options)
        : optionsFiltered.filter((opt) => opt.type !== 'divider');
    const filteredFlatCount = filteredFlatOptions.length;
    const status = isHighlighted ? 'highlight' : hasError ? 'error' : 'default';

    const getItemIndex = useCallback((opt) => filteredFlatOptions.indexOf(opt), [filteredFlatOptions]);

    return (
        <div className={cn('relative min-w-0 w-3xs max-w-full group', className)}>
            <FloatLabel size={selectSize} label={label} hasError={hasError} isOpen={isOpen} highlight={isHighlighted} />

            <button
                disabled={disabled}
                ref={mergedRef}
                type="button"
                className={cn(
                    selectVariants({ size: selectSize, status }),
                    'w-full flex items-center gap-1',
                    isOpen && 'border-primary ring-2 ring-primary/20',
                    !selected && 'text-dark-blue/50',
                )}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                {...getReferenceProps()}
            >
                {icon && <span className="shrink-0">{icon}</span>}
                <div className="truncate grow min-w-0">{selected ? selected.label : placeholder}</div>
                {enableClear && !disabled && <ButtonClear onClear={onClear} />}
                <OutlineChevronDown size={20} className="shrink-0 text-dark-blue" color="currentColor" />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <FloatingPortal>
                        <FloatingFocusManager context={context}>
                            <motion.div
                                ref={refs.setFloating}
                                style={{
                                    ...floatingStyles,
                                    position: strategy,
                                    top: y ?? 0,
                                    left: x ?? 0,
                                }}
                                className={cn(
                                    'overflow-auto rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none z-dropdown min-w-[180px]',
                                    selectSize === 'small' ? 'text-xs' : 'text-sm',
                                    dropdownClassName,
                                )}
                                tabIndex={-1}
                                {...getFloatingProps({
                                    onKeyDown(e) {
                                        if (
                                            e.key === 'Enter' &&
                                            activeIndex != null &&
                                            filteredFlatOptions[activeIndex]
                                        ) {
                                            e.preventDefault();
                                            onChange(filteredFlatOptions[activeIndex].value);
                                            setIsOpen(false);
                                        }
                                    },
                                })}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                            >
                                {enableFilter && <SelectFilter value={query} onChange={handleFilter} />}

                                {filteredFlatCount === 0 && !!query && (
                                    <div className="text-center text-gray-500 py-8 px-1.5">{t('no-results-found')}</div>
                                )}

                                {filteredFlatCount === 0 && !query && (
                                    <div className="text-center text-gray-500 py-8 px-1.5">
                                        {t('no-options-available')}
                                    </div>
                                )}

                                {filteredFlatCount > 0 && (
                                    <ul className="p-1.5">
                                        {isGrouped
                                            ? optionsFiltered.map((group, gIdx) => (
                                                  <Fragment key={group.label ?? gIdx}>
                                                      {gIdx > 0 && (
                                                          <li
                                                              className="my-1 h-[1px] bg-gray-100 mx-2"
                                                              role="separator"
                                                              aria-disabled="true"
                                                          />
                                                      )}
                                                      {group.label && (
                                                          <li
                                                              className="px-3 pt-2 pb-1 text-2xs font-semibold text-gray-400 uppercase tracking-wide select-none"
                                                              role="presentation"
                                                          >
                                                              {group.label}
                                                          </li>
                                                      )}
                                                      {group.options.map((opt) => {
                                                          const itemIdx = getItemIndex(opt);
                                                          return (
                                                              <li
                                                                  key={opt.value}
                                                                  ref={(node) => {
                                                                      listRef.current[itemIdx] = node;
                                                                  }}
                                                                  className={cn(
                                                                      'cursor-pointer select-none py-2 rounded-sm transition-colors outline-none',
                                                                      selectSize === 'small' ? 'px-2' : 'px-3',
                                                                      value === opt.value
                                                                          ? 'bg-primary/10 text-primary font-semibold'
                                                                          : activeIndex === itemIdx
                                                                            ? 'bg-gray-100'
                                                                            : 'hover:bg-gray-100',
                                                                      opt.className,
                                                                  )}
                                                                  role="option"
                                                                  aria-selected={value === opt.value}
                                                                  tabIndex={activeIndex === itemIdx ? 0 : -1}
                                                                  {...getItemProps({
                                                                      onClick() {
                                                                          onChange(opt.value);
                                                                          setIsOpen(false);
                                                                      },
                                                                      onKeyDown(e) {
                                                                          if (e.key === 'Enter') {
                                                                              e.preventDefault();
                                                                              onChange(opt.value);
                                                                              setIsOpen(false);
                                                                          }
                                                                      },
                                                                  })}
                                                              >
                                                                  {opt.label}
                                                              </li>
                                                          );
                                                      })}
                                                  </Fragment>
                                              ))
                                            : optionsFiltered.map((opt, idx) => {
                                                  if (opt.type === 'divider') {
                                                      return (
                                                          <li
                                                              key={`dividir-${idx}`}
                                                              className="my-1 h-[1px] bg-gray-200 mx-2"
                                                              role="separator"
                                                              aria-disabled="true"
                                                          />
                                                      );
                                                  }

                                                  const itemIdx = getItemIndex(opt);
                                                  return (
                                                      <li
                                                          key={opt.value}
                                                          ref={(node) => {
                                                              listRef.current[itemIdx] = node;
                                                          }}
                                                          className={cn(
                                                              'cursor-pointer select-none py-2 rounded-sm transition-colors outline-none',
                                                              selectSize === 'small' ? 'px-2' : 'px-3',
                                                              value === opt.value
                                                                  ? 'bg-primary/10 text-primary font-semibold'
                                                                  : activeIndex === itemIdx
                                                                    ? 'bg-gray-100'
                                                                    : 'hover:bg-gray-100',
                                                              opt.className,
                                                          )}
                                                          role="option"
                                                          aria-selected={value === opt.value}
                                                          tabIndex={activeIndex === itemIdx ? 0 : -1}
                                                          {...getItemProps({
                                                              onClick() {
                                                                  onChange(opt.value);
                                                                  setIsOpen(false);
                                                              },
                                                              onKeyDown(e) {
                                                                  if (e.key === 'Enter') {
                                                                      e.preventDefault();
                                                                      onChange(opt.value);
                                                                      setIsOpen(false);
                                                                  }
                                                              },
                                                          })}
                                                      >
                                                          {opt.label}
                                                      </li>
                                                  );
                                              })}
                                    </ul>
                                )}

                                {actions.length > 0 && (
                                    <div
                                        className="border-t bg-white border-gray-200 p-1.5 sticky bottom-0 gap-2"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <div className="flex flex-col gap-1">{actions}</div>
                                    </div>
                                )}
                            </motion.div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </AnimatePresence>

            <ErrorMessage message={error} />
        </div>
    );
});

export default Select;

Select.propTypes = {
    options: PropTypes.array.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf(['normal', 'small']),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    icon: PropTypes.node,
    enableClear: PropTypes.bool,
    onClear: PropTypes.func,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    enableFilter: PropTypes.bool,
    disabled: PropTypes.bool,
    highlight: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.node),
    dropdownClassName: PropTypes.string,
    dropdownWidth: PropTypes.oneOf(['trigger', 'content']),
};
